import { html } from "lit";
import logoOsm from "../img/osm.svg";
import logoWd from "../img/wd.svg";
import { OSMWikiURL, WikidataURL, PanoramaxPresetsURL, OSMURL, WikidataAPIURL } from "./services";
import { getBestLabel } from "./i18n";


// List of [ Overlay ID, API filter, Maplibre Style, Sprites ]
export const SEMANTICS_OVERLAYS = [
	[
		"features",
		"\"semantics\" IS NOT NULL",
		{
			metadata: { "name": "All features" },
			type: "circle",
			layout: {
				visibility: "none",
			},
			paint: {
				"circle-radius": 4,
				"circle-color": "#00897B"
			}
		}
	],
	[
		"tfsigns",
		"\"semantics.osm|traffic_sign\" IS NOT NULL",
		{
			metadata: { "name": "Traffic signs" },
			type: "symbol",
			layout: {
				visibility: "none",
				"icon-overlap": "never",
				"icon-image": ["case",
					// Many signs
					["in", ";", ["get", "osm|traffic_sign"]],
					"pnx-tfsigns-fr:0_many",
					// Single FR sign
					["==", ["slice", ["get", "osm|traffic_sign"], 0, 2], "FR"],
					["concat", "pnx-tfsigns-fr:", ["slice", ["get", "osm|traffic_sign"], 3]],
					// Generic value
					["==", ["get", "osm|traffic_sign"], "yes"],
					"pnx-tfsigns-fr:0_unknown",
					// Fallbacks
					["has", "osm|traffic_sign"],
					"pnx-tfsigns-fr:0_unknown",
					""
				]
			}
		},
		{ "pnx-tfsigns-fr": `${PanoramaxPresetsURL()}/sprites/trafficsigns_fr` }
	]
];

// General helpers
function valToValLink(val, url, title) {
	return html`<a href=${url} target="_blank" title=${title}>${val}</a>`;
}

// OSM helpers
const OSM_MAIN_KEYS = [
	"highway", "building", "amenity", "landuse", "natural", "waterway", "leisure",
	"shop", "railway", "tourism", "barrier", "boundary", "place", "power"
];

// Wikidata helpers
const WD_LABELS_CACHE = {};
function getWDLabel(entity, _t) {
	if(WD_LABELS_CACHE[entity]) {
		return Promise.resolve(WD_LABELS_CACHE[entity]);
	}
	else {
		const url = entity.startsWith("P") ? `properties/${entity}` : `items/${entity}/labels`;
		return fetch(`${WikidataAPIURL()}/entities/${url}`)
			.then(res => res.json())
			.then(res => {
				WD_LABELS_CACHE[entity] = getBestLabel(res.labels || res, _t.PREF_LANG);
				return WD_LABELS_CACHE[entity];
			});
	}
}

// Supported prefixes for nice semantic display
const KNOWN_PREFIXES = {
	"": {
		title: "Panoramax",
		value_transform: (tag, _t) => {
			if(tag.key === "osm") {
				return valToValLink(
					tag.value,
					`${OSMURL()}/${tag.value}`,
					_t.pnx.semantics_value_show_osm
				);
			}
			else { return tag.value; }
		}
	},

	osm: {
		title: "OpenStreetMap",
		logo: logoOsm,
		key_transform: (tag, _t) => valToValLink(
			tag.key,
			`${OSMWikiURL()}/Key:${tag.key}`,
			_t.pnx.semantics_key_doc
		),
		value_transform: (tag, _t) => {
			if(OSM_MAIN_KEYS.includes(tag.key)) {
				return valToValLink(
					tag.value,
					`${OSMWikiURL()}/Tag:${tag.key}=${tag.value}`,
					_t.pnx.semantics_value_doc
				);
			}
			else { return tag.value; }
		}
	},

	wd: {
		title: "Wikidata",
		logo: logoWd,
		key_transform: (tag, _t) => {
			const url = `${WikidataURL()}/Property:${tag.key}`;
			const keylabel = _t.pnx.semantics_wikidata_properties[tag.key];

			if(keylabel) {
				return valToValLink(
					html`${_t.pnx.semantics_wikidata_properties[tag.key]} <small>(${tag.key})</small>`,
					url,
					_t.pnx.semantics_key_doc
				);
			}
			else {
				const label = document.createElement("span");
				label.innerText = tag.key;

				// Try to fetch label from API
				getWDLabel(tag.key, _t).then(wdl => {
					label.innerHTML = `${wdl} <small>(${tag.key})</small>`;
				});

				return valToValLink(label, url, _t.pnx.semantics_key_doc);
			}
		},
		value_transform: (tag, _t) => {
			const label = document.createElement("span");
			label.innerText = tag.value;

			// Try to fetch label from API
			getWDLabel(tag.value, _t).then(wdl => {
				label.innerHTML = `${wdl} <small>(${tag.value})</small>`;
			});

			return valToValLink(
				label,
				`${WikidataURL()}/${tag.value}`,
				_t.pnx.semantics_value_doc
			);
		}
	},

	exif: { title: "EXIF" },
};


/**
 * Transform a prefix|key=value into parsed object.
 * Does not handle qualifiers tags.
 * @private
 */
export function decodeBasicTag(tag) {
	const firstEqual = (tag || "").lastIndexOf("=");
	if(firstEqual < 0) { return null; }

	const key = decodeKey(tag.substring(0, firstEqual));
	if(key.subkey.indexOf("=") >= 0) { return null; }

	return { key, value: tag.substring(firstEqual+1) };
}


/** @private */
export function decodeKey(key = "") {
	const regex = /^(?:([a-z_]+)\|)?([^[]+)(?:\[(.*)\])?$/;
	const match = key.match(regex);

	if (!match) {
		return { prefix: "", subkey: key, qualifies: null };
	}

	const [, prefix, subkey, qualifies ] = match;
	return {
		key: key,
		prefix: prefix || "",
		subkey,
		qualifies: decodeBasicTag(qualifies),
	};
}


/**
 * Transforms a string containing a list of tags in a ready-to-use parsed object list.
 * @param {string} str The string to read (each tag separated by newline `\n`)
 * @returns {object[]} List of API-formatted tags
 */
export function parseSemanticsString(str) {
	const parsedTags = str.split("\n").map(t => {
		const parts = decodeBasicTag(t);
		if(
			parts
			&& parts.key.key.length <= 256
			&& parts.value.length <= 2048
		) {
			return { key: parts.key.key, value: parts.value };
		} else { return null; }
	});

	if(parsedTags.findIndex(v => !v) >= 0) {
		if(str.trim().length === 0 && parsedTags.length === 1) { return []; }
		throw new Error("Invalid tags");
	}

	return parsedTags;
}


/**
 * Transforms a semantics array into a key=value string.
 * @param {object[]} semantics List of API-formatted tags
 * @returns {string} The readable string (each tag separated by newline `\n`)
 */
export function semanticsToString(semantics) {
	return (semantics || []).map(s => `${s.key}=${s.value}`).join("\n");
}


/**
 * Computes the difference between two set of API tags.
 * API expects a delta between old & new, so result contains only
 * added and deleted tags.
 * @param {object[]} prev The previous set of tags
 * @param {object[]} next The new set of tags
 * @returns {object[]} The new set of tags, with only added/deleted tags
 */
export function computeDiffTags(prev = [], next = []) {
	const res = [];

	// Look for new values
	(next || [])
		.filter(nt => prev.find(pt => pt.key == nt.key && pt.value == nt.value) === undefined)
		.forEach(t => res.push({key: t.key, value: t.value, action: "add"}));

	// Look for prev values missing in next
	(prev || [])
		.filter(pt => next.find(nt => pt.key == nt.key && pt.value == nt.value) === undefined)
		.forEach(t => res.push({key: t.key, value: t.value, action: "delete"}));

	return res;
}


/**
 * Transforms raw API semantics properties into ready-to-display container.
 * @param {object[]} tags The API semantics tags
 * @returns {object[]} A list of groups (by prefix), with {title, tags} information.
 */
export function groupByPrefix(tags) {
	// Create raw groups by prefix
	const byPrefix = {};
	const qualifiers = [];

	// First pass: analyze tags, separate by prefix
	tags.forEach(tag => {
		if(tag.action === "delete") { return; }
		
		const decodedKey = decodeKey(tag.key);

		// Put apart qualifiers, to later insert on tags themselves
		if(decodedKey.qualifies) {
			qualifiers.push(Object.assign({}, tag, decodedKey));
		}
		// Process classic tag
		else {
			if (!byPrefix[decodedKey.prefix]) { byPrefix[decodedKey.prefix] = []; }

			byPrefix[decodedKey.prefix].push(decodedKey.prefix.length > 0 ? {
				key: decodedKey.subkey,
				value: tag.value
			} : tag);
		}
	});

	// Second pass: add qualifiers on concerned tags
	qualifiers.forEach(({key, prefix, subkey, qualifies, value}) => {
		const concernedTag = byPrefix[qualifies.key.prefix]?.find(t => (
			t.key === qualifies.key.subkey
			&& (!qualifies.value || qualifies.value === t.value)
		));

		if(concernedTag) {
			if(!concernedTag.qualifiers) { concernedTag.qualifiers = []; }
			concernedTag.qualifiers.push({key, prefix, subkey, value});
		}
	});

	// Append known prefixes information
	let groups = Object.entries(byPrefix).map(([prefix, prefixTags]) => {
		if(KNOWN_PREFIXES[prefix]) {
			return Object.assign({ prefix, tags: prefixTags }, KNOWN_PREFIXES[prefix]);
		}
		else {
			return { prefix, title: prefix, tags: prefixTags };
		}
	});

	return groups;
}
